import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import CommonTable from "../../../components/Common/TableContainer";
import ClientPlanAddEditModal from "pages/AllCommonModal/ClientPlanAddEditModal/add-edit-client-plan-modal";
import { submitFilterData } from "model/ClientPlanModel";
import { useClientPlanContext } from "libs/context/ClientPlanContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";


const ClientPlan = () => {
  const { permissionData } = usePermissionContext() || {};
  const {
    fetchClientPlanContextData,
    clientPlanContextData,
  } = useClientPlanContext() || {};
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("Client Plan");
  const initialFilterFetchedRef = useRef(false);
  const [clientPlanAddEditModalOpen, setClientPlanAddEditModalOpen] =
    useState(false);
  const [modal1, setModal1] = useState(false);
  const toggleViewModal = () => setModal1(!modal1);
  const [searchQuery, setSearchQuery] = useState("");
  const [clientPlanData, setClientPlanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemId, setItemId] = useState(null);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const initialState = {
    title: "",
    incentiveMin: "",
    incentiveMax: "",
    status: true,
  };

  const [formData, setFormData] = useState(initialState);

  const filteredData = useMemo(() => clientPlanData, [clientPlanData]);

  const clientPlanActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.MasterClientPlanUpdateAccess),
      canEdit: Boolean(permissionData?.MasterClientPlanUpdateAccess),
    }),
    [permissionData]
  );

  const hasAnyClientPlanRowAction = useMemo(
    () => clientPlanActionPermissions.canEdit,
    [clientPlanActionPermissions]
  );

  const handleEdit = (row) => {
    setItemId(row?.id);
    setClientPlanAddEditModalOpen(true);
  };

  useEffect(() => {
    fetchClientPlanContextData();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getClientPlanList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const valueReModified = clientPlanContextData?.table?.columns?.map(
      (col) => ({
        Header: col?.title,
        accessor: col?.field,
      })
    );
    setTableMetadata(valueReModified);
  }, [clientPlanContextData]);

  const getClientPlanList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = {
      ...requestPayload,
      ...payloadOverrides,
    };
    const response = await submitFilterData(payload);
    const responseData = response?.data || {};
    const listData =
      responseData?.records ??
      (Array.isArray(responseData) ? responseData : []);
    const valueReModified = (Array.isArray(listData) ? listData : []).map(
      (item) => ({
        ...item,
        ID: item?.id,
      })
    );

    if (response) {
      setClientPlanData(valueReModified);
      setPaginationMeta({
        pageNumber:
          Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize:
          Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords:
          Number(responseData?.totalRecords) || valueReModified.length,
      });
      setRequestPayload(payload);
    } else {
      setClientPlanData([]);
    }
    setLoading(false);
  };

  const handleFilterSubmit = async (data) => {
    await getClientPlanList({
      ...data,
      search: requestPayload?.search || "",
      pageNumber: 1,
    });
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    const trimmedValue = value?.trim() || "";
    await getClientPlanList({
      ...requestPayload,
      pageNumber: 1,
      search: trimmedValue,
    });
  };

  const handlePageChange = async (page) => {
    await getClientPlanList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getClientPlanList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">Client Plan</h5>

                {clientPlanActionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button
                      color="primary"
                      onClick={() => {
                        setItemId(null);
                        setClientPlanAddEditModalOpen(true);
                        setFormData(initialState);
                      }}
                    >
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => {
                        const nextValue = e?.target?.value || "";
                        setSearchQuery(nextValue);
                        if (!nextValue.trim()) {
                          handleSearchSubmit("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyClientPlanRowAction}
                  onEdit={
                    clientPlanActionPermissions.canEdit
                      ? handleEdit
                      : undefined
                  }
                  isEditAccess={clientPlanActionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"Client Plan"}
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <ClientPlanAddEditModal
            setClientPlanAddEditModalOpen={setClientPlanAddEditModalOpen}
            clientPlanAddEditModalOpen={clientPlanAddEditModalOpen}
            getClientPlanList={getClientPlanList}
            itemId={itemId}
            setClientPlanData={setClientPlanData}
            setFormData={setFormData}
            formData={formData}
            totalData={clientPlanData}
          />
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Client Plan"
            handleSubmit={handleFilterSubmit}
            filterData={clientPlanContextData?.filters}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

ClientPlan.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(ClientPlan);
